// Copyright (c) 2025 Signal from Noise, Inc. All rights reserved.

import { err, ok, type Result } from "neverthrow";
import { proxyErrorMessage } from "~/lib/error-handling";
import type { PaginatedList } from "./MemoryService";

export interface SkillMeta {
  id: string;
  name: string;
  displayName: string;
  description: string;
  scope: "system" | "project" | "user";
  category: "skill" | "persona";
  enabled: boolean;
  /** ICON_* proto enum name (e.g., ICON_MEETING). Same format as label icons. */
  icon: string;
}

export interface Skill extends SkillMeta {
  content: string;
  context?: "agent";
  agent?: string;
}

/**
 * Which of the user's skills a typed or parsed prompt may turn into a skill mention. Not the only
 * gate: skills named in a server response are filtered on the server.
 */
export const mentionableSkills = <T extends SkillMeta>(skills: readonly T[]): T[] =>
  skills.filter((skill) => skill.enabled);

export interface CreateSkillInput {
  name?: string;
  displayName: string;
  description: string;
  content: string;
  scope?: "project" | "user";
  category?: "skill" | "persona";
  /** ICON_* proto name (e.g. ICON_MEETING), same as labels. */
  icon?: string;
  context?: "agent";
  agent?: string;
}

export interface UpdateSkillInput {
  displayName?: string;
  description?: string;
  content?: string;
  category?: "skill" | "persona";
  icon?: string;
  context?: "agent" | null;
  agent?: string | null;
}

export interface CloneSkillsInput {
  skillIds: string[];
  destination: string;
  scope?: "project" | "user";
}

interface SkillsServiceConfig {
  authReady: () => boolean;
  authToken: () => string | undefined;
  projectId: () => string | undefined;
}

export interface SkillsService {
  // Drains the cursor so callers that resolve a skill by id/name (not the
  // paged list view) still see every skill regardless of page size.
  listAll(): Promise<Result<Skill[], Error>>;
  get(id: string): Promise<Result<Skill, Error>>;
  create(input: CreateSkillInput): Promise<Result<Skill, Error>>;
  update(id: string, input: UpdateSkillInput): Promise<Result<Skill, Error>>;
  delete(id: string): Promise<Result<{ success: boolean }, Error>>;
  setPreference(input: {
    skillName: string;
    scope: string;
    enabled: boolean;
  }): Promise<Result<{ success: boolean }, Error>>;
  generateContent(input: { displayName: string; description: string }): Promise<Result<{ content: string }, Error>>;
  clone(input: CloneSkillsInput): Promise<Result<{ cloned: number; skipped: number }, Error>>;
}

export function createSkillsService(config: SkillsServiceConfig): SkillsService {
  const getHeaders = () => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    const token = config.authToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    const projectId = config.projectId();
    if (projectId) {
      headers["X-Project-Id"] = projectId;
    }
    return headers;
  };

  const apiCall = async <T>(method: string, path: string, body?: unknown): Promise<Result<T, Error>> => {
    if (!config.authReady()) {
      return err(new Error("Auth not ready"));
    }

    // Skills endpoints require a project context; defer until the URL has one
    // so callers without one don't 400 the backend.
    if (!config.projectId()) {
      return err(new Error("Project not ready"));
    }

    try {
      const response = await fetch(`/api${path}`, {
        method,
        headers: getHeaders(),
        body: body ? JSON.stringify(body) : undefined,
      });

      if (!response.ok) {
        return err(new Error(proxyErrorMessage(await response.json().catch(() => ({}))) ?? `HTTP ${response.status}`));
      }

      const data: unknown = await response.json();
      return ok(data as T);
    } catch (error) {
      return err(error instanceof Error ? error : new Error(String(error)));
    }
  };

  const listPage = async (options?: {
    limit?: number;
    afterId?: string;
    beforeId?: string;
  }): Promise<Result<PaginatedList<Skill>, Error>> => {
    const params = new URLSearchParams();
    if (options?.limit != null) params.set("limit", String(options.limit));
    if (options?.afterId != null) params.set("after_id", options.afterId);
    if (options?.beforeId != null) params.set("before_id", options.beforeId);
    const query = params.toString();
    const result = await apiCall<{
      data: Skill[];
      first_id?: string;
      last_id?: string;
      has_more: boolean;
    }>("GET", query ? `/skills?${query}` : "/skills");
    if (result.isErr()) return err(result.error);
    return ok({
      data: result.value.data,
      firstId: result.value.first_id,
      lastId: result.value.last_id,
      hasMore: result.value.has_more,
    });
  };

  return {
    listAll: async () => {
      const all: Skill[] = [];
      let afterId: string | undefined;
      do {
        const result = await listPage({ limit: 100, afterId });
        if (result.isErr()) return err(result.error);
        all.push(...result.value.data);
        afterId = result.value.hasMore ? result.value.lastId : undefined;
      } while (afterId);
      return ok(all);
    },
    get: (id) => apiCall<Skill>("GET", `/skills/${id}`),
    create: (input) => apiCall<Skill>("POST", "/skills", input),
    update: (id, input) => apiCall<Skill>("POST", `/skills/${id}`, input),
    delete: (id) => apiCall<{ success: boolean }>("DELETE", `/skills/${id}`),
    setPreference: (input) => apiCall<{ success: boolean }>("POST", "/skills/preferences", input),
    generateContent: (input) => apiCall<{ content: string }>("POST", "/skills/generate", input),
    clone: (input) => apiCall<{ cloned: number; skipped: number }>("POST", "/skills/clone", input),
  };
}
